<script setup lang="ts">
  import { computed } from 'vue'
  import { ArrowLeftIcon } from '@heroicons/vue/24/solid'
  import { ArrowLeftRight } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import ActaportDarkIcon from '@/components/icons/ActaportDarkIcon.vue'
  import ActaportSuccessIllustration from '@/components/icons/ActaportSuccessIllustration.vue'

  interface ActaportExportResult {
    actaport_case_id: string
    actaport_case_number: string
    file_name: string
    file_number: string
    last_updated: string
    participants_linked?: number
    participants_created?: number
  }

  const props = defineProps<{
    result: ActaportExportResult
    caseId: string | number
  }>()

  const { t, locale } = useTranslation({
    en: {
      backToCase: 'Back to Case',
      title: 'Actaport Case Creation Confirmation',
      successMessage: 'Actaport case created successfully.',
      fileName: 'File Name',
      fileNumber: 'File number',
      lastUpdated: 'Last updated',
      openInActaport: 'Open in Actaport',
      documentSyncInfo:
        'Documents can now be synced in both directions between Jupus and Actaport from the documents tab.'
    },
    de: {
      backToCase: 'Zurück zum Fall',
      title: 'Actaport-Fallanlage Bestätigung',
      successMessage: 'Actaport-Fall wurde erfolgreich angelegt.',
      fileName: 'Aktenname',
      fileNumber: 'Aktennummer',
      lastUpdated: 'Zuletzt aktualisiert',
      openInActaport: 'In Actaport öffnen',
      documentSyncInfo:
        'Dokumente können jetzt in beide Richtungen zwischen Jupus und Actaport über den Dokumenten-Tab synchronisiert werden.'
    }
  })

  const formattedDate = computed(() => {
    const currentLocale = locale.value === 'de' ? 'de-DE' : 'en-US'

    if (!props.result?.last_updated)
      return new Date().toLocaleDateString(currentLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })

    try {
      return new Date(props.result.last_updated).toLocaleDateString(currentLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return props.result.last_updated
    }
  })

  const actaportUrl = computed(() => {
    const caseNumber = props.result.actaport_case_number.replace('/', '-')
    return `https://app.actaport.de/akte/${caseNumber}/dashboard`
  })
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <div class="legacy-bg-color flex flex-grow items-start justify-center">
      <div class="container px-16 py-6">
        <!-- Header Section -->
        <div class="flex w-full items-center justify-between pb-5">
          <h1 class="flex-1 whitespace-nowrap text-2xl font-semibold">
            {{ t('title') }}
          </h1>
          <a
            :href="`/cases/${caseId}/`"
            class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm
              font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          >
            <ArrowLeftIcon class="h-4 w-4" />
            {{ t('backToCase') }}
          </a>
        </div>

        <!-- White Card starts here -->
        <div class="rounded-xl border bg-white p-16">
          <!-- Success Message -->
          <p class="mb-8 text-center text-lg text-gray-700">
            {{ t('successMessage') }}
          </p>

          <!-- Success Illustration -->
          <div class="mb-8 flex justify-center">
            <ActaportSuccessIllustration class="h-64 w-64" />
          </div>

          <!-- Case Details Card -->
          <div class="mx-auto max-w-2xl">
            <!-- Wrapper around File Name and Blue Section -->
            <div class="rounded-xl border border-gray-200 p-4">
              <!-- File Name Section -->
              <!-- Actaport Icon & File Name -->
              <div class="mb-4 flex items-start gap-4">
                <ActaportDarkIcon class="h-6 w-6 flex-shrink-0" />
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ t('fileName') }}: {{ result.file_name }}
                  </h3>
                </div>
              </div>

              <!-- File Number -->
              <div class="pb-4">
                <p class="text-sm text-gray-700">{{ t('fileNumber') }}: {{ result.file_number }}</p>
              </div>

              <!-- Last Updated and Open Link -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{{ t('lastUpdated') }}: {{ formattedDate }}</span>
                </div>

                <!-- Open in Actaport Button -->
                <a
                  :href="actaportUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm
                    font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  <svg
                    class="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  {{ t('openInActaport') }}
                </a>
              </div>

              <!-- Blue Section -->
              <!-- Document Sync Info -->
              <div class="mt-6 flex items-center gap-3 rounded-lg bg-blue-50 p-4">
                <ArrowLeftRight class="h-5 w-5 flex-shrink-0 text-blue-600" />
                <p class="text-sm text-blue-700">
                  {{ t('documentSyncInfo') }}
                </p>
              </div>
            </div>
          </div>
          <!-- End White Card -->
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .legacy-bg-color {
    background-color: #f8fafc !important;
  }
</style>
